// Pure financial aggregation helpers for the cliente 360 view (Ola 3, chunk 2).
// No React here: every rule (mora, próximos cobros, series mensuales) is unit-testable.
import dayjs, { type Dayjs } from 'dayjs';
import { diasEnMora, INVOICE_STATUS, INVOICE_TYPE, normalizeInvoiceStatusKey } from '@aid/shared';
import type { ClienteResponse, CobroClienteResponse, PagoClienteResponse } from '@aid/shared';
import { esVisita } from '../pagos/lib/pago-table-helpers';
import { parseDateOnly } from '../../lib/date';

export { formatearPeriodo } from '../../lib/date';
export { esVisita };

/**
 * Date-only contract: parses a DATE-backed response field component-wise
 * (legacy ISO-midnight values recover their UTC calendar date). Falls back to
 * the raw parse for invalid values so pre-existing behavior never changes.
 */
function fechaDayjs(value: Date | string | null | undefined): Dayjs {
  return parseDateOnly(value) ?? dayjs(value as Date | string | null | undefined);
}

export interface BucketMensual {
  /** YYYY-MM bucket key. */
  mes: string;
  facturado: number;
  pagado: number;
}

/** Latest 6 records by id (the list endpoint orders by id DESC). */
export function ultimasFacturas(cobros: CobroClienteResponse[], n = 6): CobroClienteResponse[] {
  return cobros.slice(0, n);
}

/** Latest 6 records by id (the list endpoint orders by id DESC). */
export function ultimosPagos(pagos: PagoClienteResponse[], n = 6): PagoClienteResponse[] {
  return pagos.slice(0, n);
}

/**
 * True when the bill is still owed. Anulled bills never count. When the
 * backend resolved `estado` (by-client endpoint of the 360 view), only
 * VIGENTE is pending. Unknown catalog state is fail-safe and never counted as
 * pending; invoice status must not be inferred from an environment-specific id.
 */
export function esFacturaPendiente(c: CobroClienteResponse): boolean {
  if (c.motivoAnulacion) return false;
  return normalizeInvoiceStatusKey(c.estado?.key) === INVOICE_STATUS.VIGENTE;
}

/** Extraordinary charges have no billing period and must not drive cycle KPIs. */
function esFacturaPeriodica(c: CobroClienteResponse): boolean {
  return c.tipoFactura == null || c.tipoFactura === INVOICE_TYPE.PERIODICA;
}

/** Oldest pending bill by period start (the anchor for mora). */
export function facturaVigenteMasAntigua(
  cobros: CobroClienteResponse[],
): CobroClienteResponse | null {
  let oldest: CobroClienteResponse | null = null;
  for (const c of cobros) {
    if (!esFacturaPendiente(c)) continue;
    if (!esFacturaPeriodica(c) || !c.fechaInicio) continue;
    if (!oldest || fechaDayjs(c.fechaInicio).isBefore(fechaDayjs(oldest.fechaInicio))) oldest = c;
  }
  return oldest;
}

/**
 * Most recent payment with value > 0 that was not annulled. Seller visits
 * (Q0) never count as payments. Compares by fechaPago so the result does not
 * depend on the endpoint ordering.
 */
export function ultimoPagoConValor(pagos: PagoClienteResponse[]): PagoClienteResponse | null {
  let last: PagoClienteResponse | null = null;
  for (const p of pagos) {
    if (p.motivoAnulacion) continue;
    if (esVisita(p)) continue;
    if (!last || dayjs(p.fechaPago).isAfter(dayjs(last.fechaPago))) last = p;
  }
  return last;
}

/** Whole days elapsed between a payment date and hoy (clamped to >= 0). */
export function diasDesdeUltimoPago(fecha: Date, hoy: Dayjs = dayjs()): number {
  return Math.max(hoy.startOf('day').diff(dayjs(fecha).startOf('day'), 'day'), 0);
}

/** Most recent pending bill by charge date (reference for the next cobro). */
export function ultimaFacturaVigente(cobros: CobroClienteResponse[]): CobroClienteResponse | null {
  let last: CobroClienteResponse | null = null;
  for (const c of cobros) {
    if (!esFacturaPendiente(c)) continue;
    if (!esFacturaPeriodica(c)) continue;
    if (!last || fechaDayjs(c.fechaCobro).isAfter(fechaDayjs(last.fechaCobro))) last = c;
  }
  return last;
}

/**
 * Past-due pending bills: those whose period ended before the current month.
 * Anchored on the PERIOD end (fechaFin), not fechaCobro — the known
 * fecha_cobro drift never moves an old bill into the current month.
 */
function facturasMoraVencida(cobros: CobroClienteResponse[], hoy: Dayjs): CobroClienteResponse[] {
  const inicioMes = hoy.startOf('month');
  return cobros.filter(
    (c) =>
      esFacturaPeriodica(c) &&
      c.fechaAnulacion === null &&
      !c.motivoAnulacion &&
      saldoPendienteFactura(c) > 0 &&
      c.fechaFin !== null &&
      fechaDayjs(c.fechaFin).isBefore(inicioMes),
  );
}

/** Canonical non-negative remainder exposed by the cliente 360 cobros API. */
function saldoPendienteFactura(cobro: CobroClienteResponse): number {
  const saldo = Number(cobro.balance?.saldoPendiente ?? 0);
  return Number.isFinite(saldo) ? Math.max(saldo, 0) : 0;
}

/** Oldest past-due invoice with a positive canonical remainder. */
export function facturaMoraVencidaMasAntigua(
  cobros: CobroClienteResponse[],
  hoy: Dayjs = dayjs(),
): CobroClienteResponse | null {
  let oldest: CobroClienteResponse | null = null;
  for (const cobro of facturasMoraVencida(cobros, hoy)) {
    if (!oldest || fechaDayjs(cobro.fechaInicio).isBefore(fechaDayjs(oldest.fechaInicio))) {
      oldest = cobro;
    }
  }
  return oldest;
}

/** Sum of past-due canonical invoice remainders. */
export function montoMoraVencida(cobros: CobroClienteResponse[], hoy: Dayjs = dayjs()): number {
  let total = 0;
  for (const c of facturasMoraVencida(cobros, hoy)) total += saldoPendienteFactura(c);
  return total;
}

/** Count of past-due invoices with a positive canonical remainder. */
export function nFacturasVencidas(cobros: CobroClienteResponse[], hoy: Dayjs = dayjs()): number {
  return facturasMoraVencida(cobros, hoy).length;
}

export interface ResumenPendienteMes {
  monto: number;
  n: number;
}

/**
 * Current-month pending amount and bill count: pending bills whose period
 * covers the current month (started on/before today and ending on/after the
 * 1st). Period-anchored, so the known fecha_cobro → next-month drift never
 * drops the current bill from its own month.
 */
export function pendienteMesActual(
  cobros: CobroClienteResponse[],
  hoy: Dayjs = dayjs(),
): ResumenPendienteMes {
  const inicioMes = hoy.startOf('month');
  let monto = 0;
  let n = 0;
  for (const c of cobros) {
    if (!esFacturaPendiente(c)) continue;
    if (!esFacturaPeriodica(c) || !c.fechaInicio || !c.fechaFin) continue;
    if (fechaDayjs(c.fechaInicio).isAfter(hoy)) continue;
    if (fechaDayjs(c.fechaFin).isBefore(inicioMes)) continue;
    monto += saldoPendienteFactura(c);
    n += 1;
  }
  return { monto, n };
}

/**
 * Days of overdue debt driven by the POSITIVE deudaPendiente contract
 * (view_client_balance): 0 when there is no open debt (deuda <= 0) or the
 * oldest pending bill is still within its 15-day grace period (the due date
 * is charge date + DIAS_GRACIA_FACTURA); whole days elapsed since the due
 * date when deuda > 0, and null when the debt cannot be anchored to a date.
 * Regression guard: the legacy signed saldo (-N in debt) must never be passed
 * here — with the current positive contract it made every indebted client
 * read as "Al día".
 * The grace rule lives in @aid/shared (reglas-financieras) so backend and
 * frontend share the same source of truth.
 */
export function calcularDiasMora(
  deuda: number | undefined,
  facturaAntigua: CobroClienteResponse | null,
  cliente: ClienteResponse,
  hoy: Dayjs = dayjs(),
): number | null {
  if (deuda === undefined) return null;
  if (deuda <= 0) return 0;
  const base = facturaAntigua ? facturaAntigua.fechaCobro : cliente.fechaCobro;
  if (!base) return null;
  return diasEnMora(base, hoy.toDate());
}

/**
 * Oldest pending bill of the current period by charge date (fechaCobro) —
 * the anchor for the "Vence el" caption of the factura del período KPI.
 * Mirrors pendienteMesActual's period filter (started on/before today and
 * ending on/after the 1st) so it never shows a due date for a bill the
 * period card does not count.
 */
export function facturaPeriodoMasAntigua(
  cobros: CobroClienteResponse[],
  hoy: Dayjs = dayjs(),
): CobroClienteResponse | null {
  const inicioMes = hoy.startOf('month');
  let oldest: CobroClienteResponse | null = null;
  for (const c of cobros) {
    if (!esFacturaPendiente(c)) continue;
    if (!esFacturaPeriodica(c) || !c.fechaInicio || !c.fechaFin) continue;
    if (fechaDayjs(c.fechaInicio).isAfter(hoy)) continue;
    if (fechaDayjs(c.fechaFin).isBefore(inicioMes)) continue;
    if (!oldest || fechaDayjs(c.fechaCobro).isBefore(fechaDayjs(oldest.fechaCobro))) oldest = c;
  }
  return oldest;
}

/** Active or theoretical billing period (see periodoActivoFacturacion). */
export interface PeriodoFacturacion {
  /** Date-only values (DateOnlyString contract) — never UTC-midnight instants. */
  fechaInicio: string | null;
  fechaFin: string | null;
  /** True when no bill covers the current month and the range is theoretical. */
  sinFacturar: boolean;
}

/**
 * Active billing period: the period of the current-month pending bill — the
 * same selection as pendienteMesActual/facturaPeriodoMasAntigua (period that
 * covers the current month). When no bill covers the month (client 793),
 * returns the THEORETICAL cycle range (fechaCobro − 1 month → fechaCobro)
 * with sinFacturar: true.
 */
export function periodoActivoFacturacion(
  cobros: CobroClienteResponse[],
  cliente: ClienteResponse,
  hoy: Dayjs = dayjs(),
): PeriodoFacturacion {
  const factura = facturaPeriodoMasAntigua(cobros, hoy);
  if (factura) {
    return { fechaInicio: factura.fechaInicio, fechaFin: factura.fechaFin, sinFacturar: false };
  }
  if (!cliente.fechaCobro) return { fechaInicio: null, fechaFin: null, sinFacturar: true };
  const fin = fechaDayjs(cliente.fechaCobro).startOf('day');
  return {
    fechaInicio: fin.subtract(1, 'month').format('YYYY-MM-DD'),
    fechaFin: fin.format('YYYY-MM-DD'),
    sinFacturar: true,
  };
}

/**
 * Next billing period: starts at the client's next charge date (fechaCobro)
 * and spans one month (fechaFin = fechaCobro + 1 month, start of day). Both
 * null when the client has no fechaCobro. Date-only values (DateOnlyString).
 */
export function proximoPeriodoFacturacion(cliente: ClienteResponse): {
  fechaInicio: string | null;
  fechaFin: string | null;
} {
  if (!cliente.fechaCobro) return { fechaInicio: null, fechaFin: null };
  const inicio = fechaDayjs(cliente.fechaCobro).startOf('day');
  return {
    fechaInicio: inicio.format('YYYY-MM-DD'),
    fechaFin: inicio.add(1, 'month').format('YYYY-MM-DD'),
  };
}

/**
 * Billing anchor derived from a picked billing date. Mirrors the backend
 * rule (deriveDiaCobro): a 29/30/31 anchor survives the last day of a short
 * month (Sep 30 keeps 31, Feb 28 keeps 30), any other date yields its
 * calendar day. Returns null while the date is empty or invalid.
 */
export function derivarDiaCobro(
  fecha: Dayjs | string | Date | null | undefined,
  anchorAnterior?: number | null,
): number | null {
  if (fecha == null) return null;
  const fechaValida = dayjs.isDayjs(fecha) ? fecha : fechaDayjs(fecha);
  if (!fechaValida.isValid()) return null;

  const dia = fechaValida.date();
  if (anchorAnterior != null && anchorAnterior >= 29 && anchorAnterior <= 31) {
    if (dia < anchorAnterior && dia === fechaValida.daysInMonth()) {
      return anchorAnterior;
    }
  }
  return dia;
}

/**
 * Next charge date: the client's charge day of month (diaCobro, falling back
 * to the day of fechaCobro) in the current month when it is still ahead,
 * otherwise the next month — clamping to the last day of the month.
 */
export function proximoCobro(cliente: ClienteResponse, hoy: Dayjs = dayjs()): Date | null {
  const dia =
    cliente.diaCobro ?? (cliente.fechaCobro ? fechaDayjs(cliente.fechaCobro).date() : null);
  if (!dia) return null;
  const monthTarget = (base: Dayjs): Dayjs => base.date(Math.min(dia, base.daysInMonth()));
  const esteMes = monthTarget(hoy.startOf('day'));
  if (!esteMes.isBefore(hoy.startOf('day'))) return esteMes.toDate();
  return monthTarget(hoy.startOf('day').add(1, 'month')).toDate();
}

/**
 * Facturado vs pagado buckets covering the FULL activity range: from the
 * month of the oldest non-annulled record to the month of the most recent
 * one, with a minimum window so charts never render distorted bars for a
 * client with very little history. The range ends at the last activity, not
 * today: a long tail of empty months after a dormant client adds noise
 * without information. Empty leading months (before the first activity) are
 * padded with zeros up to `mesesMinimos` so the chart keeps its proportions.
 */
export function serieMensualCompleta(
  cobros: CobroClienteResponse[],
  pagos: PagoClienteResponse[],
  mesesMinimos = 6,
): BucketMensual[] {
  const fechas: Dayjs[] = [];
  for (const c of cobros) {
    if (!c.motivoAnulacion) fechas.push(fechaDayjs(c.fechaCobro));
  }
  for (const p of pagos) {
    if (!p.motivoAnulacion) fechas.push(dayjs(p.fechaPago));
  }
  if (fechas.length === 0) return [];

  const inicio = fechas.reduce((min, f) => (f.isBefore(min) ? f : min)).startOf('month');
  const fin = fechas.reduce((max, f) => (f.isAfter(max) ? f : max)).startOf('month');
  const inicioReal = inicio;
  const rangoMeses = fin.diff(inicioReal, 'month') + 1;
  // Si el historial es más corto que la ventana mínima, estira el inicio
  // hacia atrás (con ceros) para que las barras no se vean distorsionadas.
  const cursorInicial =
    rangoMeses < mesesMinimos ? fin.subtract(mesesMinimos - 1, 'month') : inicioReal;
  const buckets: BucketMensual[] = [];
  const index = new Map<string, number>();
  let cursor = cursorInicial;
  while (!cursor.isAfter(fin)) {
    const mes = cursor.format('YYYY-MM');
    index.set(mes, buckets.length);
    buckets.push({ mes, facturado: 0, pagado: 0 });
    cursor = cursor.add(1, 'month');
  }
  for (const c of cobros) {
    if (c.motivoAnulacion) continue;
    const i = index.get(fechaDayjs(c.fechaCobro).format('YYYY-MM'));
    if (i !== undefined) buckets[i]!.facturado += Number.parseFloat(c.valor) || 0;
  }
  for (const p of pagos) {
    if (p.motivoAnulacion) continue;
    const i = index.get(dayjs(p.fechaPago).format('YYYY-MM'));
    if (i !== undefined) buckets[i]!.pagado += Number.parseFloat(p.valor) || 0;
  }
  return buckets;
}

/** Facturado vs pagado buckets for the last N months (annulled records excluded). */
export function serieMensual(
  cobros: CobroClienteResponse[],
  pagos: PagoClienteResponse[],
  meses = 6,
  hoy: Dayjs = dayjs(),
): BucketMensual[] {
  const inicio = hoy.startOf('month');
  const buckets: BucketMensual[] = [];
  for (let i = meses - 1; i >= 0; i--) {
    const m = inicio.subtract(i, 'month');
    buckets.push({ mes: m.format('YYYY-MM'), facturado: 0, pagado: 0 });
  }
  const index = new Map(buckets.map((b, i) => [b.mes, i]));
  for (const c of cobros) {
    if (c.motivoAnulacion) continue;
    const i = index.get(fechaDayjs(c.fechaCobro).format('YYYY-MM'));
    if (i !== undefined) buckets[i]!.facturado += Number.parseFloat(c.valor) || 0;
  }
  for (const p of pagos) {
    if (p.motivoAnulacion) continue;
    const i = index.get(dayjs(p.fechaPago).format('YYYY-MM'));
    if (i !== undefined) buckets[i]!.pagado += Number.parseFloat(p.valor) || 0;
  }
  return buckets;
}
